/*    
<summary>
  This functional component "AddCategoryManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../contexts/store-provider";
import { formatMessage } from "../../../translations/format-message";
import {
  IInventoryList,
  IUpdateInventory,
} from "../../../models/response/IInventoryManagementResponse";
import { emptyValueNA } from "../../../constants/constants";
import addInventoryTypeEnum from "../../../constants/enums/add-inventory-type-enum";
import AddInventoryInfoForm from "../forms/add-inventory-info-form";

interface IProps {
  storeId: number;
  addType: addInventoryTypeEnum;
  selectedData: IInventoryList | undefined;
  modalClosed: () => void;
}

const AddInventoryInfo = (props: IProps) => {
  const { inventoryStore } = useStore();
  const { selectedData, addType, storeId } = props;
  const {
    AddInventoryService,
    getInventorytByIdState,
    addUpdateInventorytState,
    resetAddUpdateInventory,
  } = inventoryStore;

  const initialValues: IUpdateInventory = {
    ProductName:
      selectedData && selectedData?.ProductName
        ? selectedData.ProductName
        : "",
    Barcode:
      selectedData && selectedData?.Barcode
        ? selectedData.Barcode
        : "",
    AddType: addType,
    AdjustedQuantity: "",
    IncomingQuantity: "",
    InternalUsedQuantity: "",
    Notes: "",
    IsNegative: "0"
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateInventorytState.success) {
      toast.success(formatMessage("added_success"));
      onModalClosed();
    }
  }, [addUpdateInventorytState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStaffManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateInventorytState.error) {
      toast.error(formatMessage(addUpdateInventorytState.error));
      resetAddUpdateInventory();
    }
  }, [addUpdateInventorytState.error]);

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: any) => {
    AddInventoryService({
      ...values,
      ProductId: selectedData?.ProductId,
      StoreId: storeId,
    });
  };

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddInventoryInfoForm
        addType={addType}
        initialValues={initialValues}
        onModalClosed={onModalClosed}
        submitHandler={onSubmitForm}
        isLoading={addUpdateInventorytState.inProgress}
      />
    </React.Fragment>
  );
};

export default observer(AddInventoryInfo);
